/**
 * Lista de devices com filtros simples e ação "ver detalhes".
 */
import { useEffect, useMemo, useState } from "react";
import RelayBadge from "../components/RelayBadge.jsx";
import RssiBadge from "../components/RssiBadge.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import HeapBadge from "../components/HeapBadge.jsx";
import {
  DEVICE_NAME_KIND_LABEL,
  deviceDisplayName,
  deviceStatus,
  formatRelative,
  formatUptime,
  offlineAgingLevel,
  pickPayload,
} from "../lib/format.js";
import {
  archiveDevice,
  authorizeDevice,
  blockDevice,
  listDevices,
  listDevicesWithIxcBinding,
  restoreDevice,
  revokeDevice,
} from "../lib/api.js";
import { isPlatformAdmin } from "../lib/rbac.js";
import { useCriticalConfirm } from "../components/ConfirmCriticalAction.jsx";
import { t } from "../i18n/index.js";
import { extractPowerMonitor, powerChipText, powerStatusLabel } from "../lib/powerMonitor.js";
import EnergyHistoryPanel from "../components/EnergyHistoryPanel.jsx";

export default function DevicesList({ devices, loading, onOpenDetail, onRefresh }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | online | degraded | offline
  // S3.11B — arquivar/restaurar é só de plataforma (super_admin/bancada).
  const canArchive = isPlatformAdmin();
  // S3.10B — o hook DEVE ficar no mesmo escopo de uso (esbuild não pega var
  // fora de escopo; viraria ReferenceError em runtime).
  const confirmCritical = useCriticalConfirm();

  // Sprint 5.0.3 — Set de device_ids com binding IXC ativo (para
  // pintar badge "IXC" na lista). Fetch único; falha silenciosa
  // (lista funciona sem o badge se endpoint estiver indisponível).
  //
  // Hotfix S5.0.3A — deps [] em vez de [devices]:
  //   Com [devices], cada render que muda a referência do array
  //   (filtro mudou, paginação, etc) dispara refetch — desnecessário
  //   e gera flickering. Para invalidar o cache após bind, uma futura
  //   sprint adicionará uma prop refreshKey controlada pelo pai.
  const [ixcBoundIds, setIxcBoundIds] = useState(() => new Set());
  useEffect(() => {
    const ac = new AbortController();
    listDevicesWithIxcBinding({ signal: ac.signal })
      .then((data) => {
        const list = Array.isArray(data?.device_ids) ? data.device_ids : [];
        setIxcBoundIds(new Set(list));
      })
      .catch(() => {
        // silencioso — badge fica oculto, mas lista funciona
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // S3.11B — "Mostrar arquivados": a prop `devices` (auto-refresh do App) traz
  // só ativos (backend filtra archived_at NULL por padrão). Quando ligado,
  // buscamos a lista completa (ativos + arquivados) localmente. `localTick`
  // força refetch após arquivar/restaurar.
  const [showArchived, setShowArchived] = useState(false);
  const [archivedList, setArchivedList] = useState(null);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [localTick, setLocalTick] = useState(0);
  useEffect(() => {
    if (!showArchived) {
      setArchivedList(null);
      return;
    }
    const ac = new AbortController();
    setArchivedLoading(true);
    listDevices({ includeArchived: true, signal: ac.signal })
      .then((list) => setArchivedList(Array.isArray(list) ? list : []))
      .catch(() => {
        // silencioso — cai de volta para a lista ativa abaixo
      })
      .finally(() => setArchivedLoading(false));
    return () => ac.abort();
  }, [showArchived, localTick]);

  // S3.11C — "Mostrar pendentes/bloqueados": busca authorization=all (inclui
  // pending/blocked, normalmente ocultos). Tem precedência sobre arquivados.
  const [showAuth, setShowAuth] = useState(false);
  const [authList, setAuthList] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  useEffect(() => {
    if (!showAuth) {
      setAuthList(null);
      return;
    }
    const ac = new AbortController();
    setAuthLoading(true);
    listDevices({ authorization: "all", signal: ac.signal })
      .then((list) => setAuthList(Array.isArray(list) ? list : []))
      .catch(() => {
        // silencioso
      })
      .finally(() => setAuthLoading(false));
    return () => ac.abort();
  }, [showAuth, localTick]);

  // Fonte exibida: auth (pending/blocked) > arquivados > ativos.
  const source = showAuth
    ? authList || []
    : showArchived
      ? archivedList || []
      : devices || [];
  const sourceLoading = showAuth
    ? authLoading
    : showArchived
      ? archivedLoading
      : loading;

  // Ações de archive/restore (super_admin). Confirmação forte reutilizando a
  // infra S3.10 (senha do usuário OU ADMIN_TOKEN da bancada).
  const [busyId, setBusyId] = useState(null);
  const [actionErr, setActionErr] = useState(null);

  async function onArchive(device) {
    setActionErr(null);
    const res = await confirmCritical({
      title: "Arquivar dispositivo",
      actionLabel: "Arquivar (soft-delete)",
      target: { deviceId: device.device_id },
      impact:
        "O dispositivo sai das listas operacionais (frota, alertas, automação, OTA). " +
        "Histórico, telemetria, comandos, agenda e auditoria são preservados. Reversível por Restaurar.",
      collectReason: true,
      reasonLabel: "Motivo do arquivamento (opcional)",
      reasonPlaceholder: "Ex.: fim de contrato, equipamento devolvido…",
    });
    if (res == null) return; // cancelado
    try {
      setBusyId(device.id);
      await archiveDevice(device.device_id, {
        reason: res.reason,
        confirm: res.secret,
      });
      setLocalTick((n) => n + 1); // refetch lista de arquivados
      onRefresh?.(); // refetch lista ativa (App)
    } catch (e) {
      setActionErr(e?.message || "Falha ao arquivar dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  async function onRestore(device) {
    setActionErr(null);
    const secret = await confirmCritical({
      title: "Restaurar dispositivo",
      actionLabel: "Restaurar",
      target: { deviceId: device.device_id },
      impact: "O dispositivo volta a aparecer nas listas operacionais.",
    });
    if (secret == null) return; // cancelado
    try {
      setBusyId(device.id);
      await restoreDevice(device.device_id, { confirm: secret });
      setLocalTick((n) => n + 1);
      onRefresh?.();
    } catch (e) {
      setActionErr(e?.message || "Falha ao restaurar dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  // S3.11C — gate de autorização (super_admin/bancada). Reusa a confirmação S3.10.
  async function onAuthorize(device) {
    setActionErr(null);
    const res = await confirmCritical({
      title: "Autorizar dispositivo",
      actionLabel: "Autorizar (tornar operacional)",
      target: { deviceId: device.device_id },
      impact:
        "Passa a aparecer nas listas/mapa e a receber comando/OTA/template/" +
        "automação/alerta. Vincula binding pendente + aplica template, se houver.",
      collectReason: true,
      reasonLabel: "Motivo (opcional)",
    });
    if (res == null) return;
    try {
      setBusyId(device.id);
      await authorizeDevice(device.device_id, {
        reason: res.reason,
        confirm: res.secret,
      });
      setLocalTick((n) => n + 1);
      onRefresh?.();
    } catch (e) {
      setActionErr(e?.message || "Falha ao autorizar dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  async function onBlock(device) {
    setActionErr(null);
    const res = await confirmCritical({
      title: "Bloquear dispositivo",
      actionLabel: "Bloquear (negar)",
      target: { deviceId: device.device_id },
      impact:
        "Fica oculto e não recebe nada operacional. Pode ser autorizado depois.",
      collectReason: true,
      reasonLabel: "Motivo do bloqueio (opcional)",
    });
    if (res == null) return;
    try {
      setBusyId(device.id);
      await blockDevice(device.device_id, {
        reason: res.reason,
        confirm: res.secret,
      });
      setLocalTick((n) => n + 1);
      onRefresh?.();
    } catch (e) {
      setActionErr(e?.message || "Falha ao bloquear dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  async function onRevoke(device) {
    setActionErr(null);
    const res = await confirmCritical({
      title: "Revogar autorização",
      actionLabel: "Revogar (volta para pendente)",
      target: { deviceId: device.device_id },
      impact: "Sai das listas operacionais e volta para a fila de autorização.",
      collectReason: true,
      reasonLabel: "Motivo (opcional)",
    });
    if (res == null) return;
    try {
      setBusyId(device.id);
      await revokeDevice(device.device_id, {
        reason: res.reason,
        confirm: res.secret,
      });
      setLocalTick((n) => n + 1);
      onRefresh?.();
    } catch (e) {
      setActionErr(e?.message || "Falha ao revogar dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return source.filter((d) => {
      // Status filter
      if (statusFilter !== "all" && deviceStatus(d) !== statusFilter) {
        return false;
      }
      // Text filter — match em vários campos
      if (q) {
        const hay = [
          d.device_id,
          d.customer_code,
          d.fw_version,
          d.ip_last,
          pickPayload(d, "ip"),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [source, query, statusFilter]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <input
            type="search"
            placeholder={t("devicesList.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input"
          />
        </div>
        <FilterChip
          label={t("devicesList.all")}
          count={source.length}
          active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
        />
        <FilterChip
          label={t("devicesList.online")}
          count={source.filter((d) => deviceStatus(d) === "online").length}
          tone="ok"
          active={statusFilter === "online"}
          onClick={() => setStatusFilter("online")}
        />
        <FilterChip
          label={t("devicesList.degraded")}
          count={source.filter((d) => deviceStatus(d) === "degraded").length}
          tone="warn"
          active={statusFilter === "degraded"}
          onClick={() => setStatusFilter("degraded")}
        />
        <FilterChip
          label={t("devicesList.offline")}
          count={source.filter((d) => deviceStatus(d) === "offline").length}
          tone="err"
          active={statusFilter === "offline"}
          onClick={() => setStatusFilter("offline")}
        />
        {canArchive && (
          <label className="flex items-center gap-2 text-sm text-noc-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-[#FFB224]"
            />
            Mostrar arquivados
          </label>
        )}
        {canArchive && (
          <label className="flex items-center gap-2 text-sm text-noc-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showAuth}
              onChange={(e) => setShowAuth(e.target.checked)}
              className="accent-[#35E8A4]"
            />
            Mostrar pendentes/bloqueados
          </label>
        )}
      </div>

      {actionErr && (
        <div className="card-pad border border-noc-err/30 bg-noc-err/10 text-sm text-noc-err">
          {actionErr}
        </div>
      )}

      {sourceLoading && source.length === 0 ? (
        <div className="card-pad text-noc-muted">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="card-pad text-noc-muted">
          {source.length > 0
            ? t("devicesList.noMatch")
            : t("devicesList.empty")}
        </div>
      ) : (
        <DeviceTable
          devices={filtered}
          onOpenDetail={onOpenDetail}
          ixcBoundIds={ixcBoundIds}
          canArchive={canArchive}
          onArchive={onArchive}
          onRestore={onRestore}
          authView={showAuth}
          onAuthorize={onAuthorize}
          onBlock={onBlock}
          onRevoke={onRevoke}
          busyId={busyId}
        />
      )}
    </div>
  );
}

function FilterChip({ label, count, active, tone = "default", onClick }) {
  // Command Deck — chip mono com borda na cor do estado; o ativo
  // inverte (fundo ciano sólido, texto escuro).
  const toneText =
    tone === "ok"
      ? "text-noc-ok border-noc-ok/35 hover:bg-noc-ok/10"
      : tone === "warn"
        ? "text-noc-warn border-noc-warn/35 hover:bg-noc-warn/10"
        : tone === "err"
          ? "text-noc-err border-noc-err/35 hover:bg-noc-err/10"
          : "text-noc-text2 border-noc-border hover:bg-noc-panel2";
  return (
    <button
      onClick={onClick}
      className={
        "chip-filter uppercase "
        + (active
            ? "bg-noc-accent border-noc-accent text-noc-bg font-bold"
            : toneText)
      }
    >
      {label} <span className={active ? "opacity-80" : "opacity-60"}>{count}</span>
    </button>
  );
}

function DeviceTable({
  devices,
  onOpenDetail,
  ixcBoundIds = new Set(),
  canArchive = false,
  onArchive,
  onRestore,
  authView = false,
  onAuthorize,
  onBlock,
  onRevoke,
  busyId = null,
}) {
  // S3.11B — linhas expandíveis: detalhes técnicos saem das colunas (evita
  // barra horizontal no desktop) e aparecem sob demanda numa linha extra.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  // ENERGY-HISTORY-VISIBILITY — abrir (nunca colapsar) a linha p/ revelar o
  // painel Energia 24h ao clicar no chip/botão de energia.
  const showEnergy = (id) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.add(id);
      return n;
    });

  return (
    <div className="card overflow-hidden">
      {/* Desktop (md+): tabela. Telas estreitas não cabem a tabela sem cortar —
          escondemos a tabela e mostramos cards empilhados (md:hidden) abaixo. */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint bg-noc-bg/60">
            <tr>
              <Th className="w-8" />
              <Th>{t("devicesList.colStatus")}</Th>
              <Th>{t("devicesList.colDeviceId")}</Th>
              <Th className="hidden lg:table-cell">{t("devicesList.colCustomer")}</Th>
              <Th>{t("devicesList.colFw")}</Th>
              <Th>{t("devicesList.colRelay")}</Th>
              <Th>{t("devicesList.colLastSeen")}</Th>
              <Th className="text-right" />
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <DeviceRow
                key={d.id}
                device={d}
                onOpenDetail={onOpenDetail}
                hasIxc={ixcBoundIds?.has?.(d.device_id) || false}
                expanded={expanded.has(d.id)}
                onToggle={() => toggle(d.id)}
                onShowEnergy={() => showEnergy(d.id)}
                canArchive={canArchive}
                onArchive={onArchive}
                onRestore={onRestore}
                authView={authView}
                onAuthorize={onAuthorize}
                onBlock={onBlock}
                onRevoke={onRevoke}
                busy={busyId === d.id}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile (< md): cards empilhados, sem rolagem horizontal. */}
      <div className="md:hidden divide-y divide-noc-divider">
        {devices.map((d) => (
          <DeviceMobileCard
            key={d.id}
            device={d}
            onOpenDetail={onOpenDetail}
            hasIxc={ixcBoundIds?.has?.(d.device_id) || false}
            canArchive={canArchive}
            onArchive={onArchive}
            onRestore={onRestore}
            authView={authView}
            onAuthorize={onAuthorize}
            onBlock={onBlock}
            onRevoke={onRevoke}
            busy={busyId === d.id}
          />
        ))}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left font-medium px-2.5 py-2 whitespace-nowrap ${className}`}>{children}</th>;
}
function Td({ children, mono = false, className = "" }) {
  return (
    <td
      className={`px-2.5 py-2 whitespace-nowrap ${mono ? "font-mono text-xs" : ""} ${className}`}
    >
      {children}
    </td>
  );
}

/** NOC-UX — última comunicação compacta: tempo relativo SEMPRE (nunca repete
 *  "online" que o StatusBadge já mostra), colorido pelo nível de envelhecimento. */
function LastSeenCell({ lastSeen }) {
  const level = offlineAgingLevel(lastSeen);
  const color =
    level === "online"
      ? "text-noc-ok"
      : level === "warn"
        ? "text-noc-warn"
        : level === "alert"
          ? "text-noc-orange"
          : "text-noc-muted";
  return (
    <span
      className={`text-xs ${color}`}
      title={lastSeen ? new Date(lastSeen).toLocaleString("pt-BR") : "—"}
    >
      {lastSeen ? formatRelative(lastSeen) : "—"}
    </span>
  );
}

/** S3.11B — item de detalhe técnico (linha expandível). */
function Detail({ label, children }) {
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <span className="font-mono text-noc-faint uppercase text-[9px] tracking-[0.12em]">{label}:</span>
      <span className="text-noc-text">{children}</span>
    </div>
  );
}

/** NOC-UX — selos de identidade/estado do device. Compartilhado entre a tabela
 *  (desktop) e o card (mobile) p/ ficar 1 fonte só. */
function DeviceBadges({ hasIxc = false, device, archived = false, authStatus = "authorized" }) {
  const cls = "ml-1.5 inline-flex items-center text-[9px] font-bold px-1 py-0.5 rounded border align-middle";
  return (
    <>
      {hasIxc && (
        <span className={`${cls} border-noc-accent/30 text-noc-accent bg-noc-accent/10`} title={t("devicesList.ixcBindingTooltip")}>
          IXC
        </span>
      )}
      {/* produção protegida (denylist OTA) — só informativo; a trava é no backend. */}
      {device.ota_protected && (
        <span className={`${cls} border-violet-400/40 text-violet-300 bg-violet-400/10`} title="Produção protegida — denylist OTA. Comandos OTA/relé/reboot/config bloqueados.">
          🔒 Protegido
        </span>
      )}
      {archived && (
        <span className={`${cls} border-noc-warn/30 text-noc-warn bg-noc-warn/10`} title={device.archive_reason || "Dispositivo arquivado (soft-delete)"}>
          ARQUIVADO
        </span>
      )}
      {authStatus === "pending" && (
        <span className={`${cls} border-noc-warn/30 text-noc-warn bg-noc-warn/10`} title="Aguardando autorização do NOC">
          PENDENTE
        </span>
      )}
      {authStatus === "blocked" && (
        <span className={`${cls} border-noc-err/30 text-noc-err bg-noc-err/10`} title={device.authorization_reason || "Dispositivo bloqueado"}>
          BLOQUEADO
        </span>
      )}
    </>
  );
}

/** NOC-UX-DEVICES-IDENTITY-1 — identidade do device com o nome AMIGÁVEL em
 *  destaque (login PPPoE do cliente IXC ou nome manual do ponto) no lugar do
 *  device_id. O device_id (chave de hardware) vira linha secundária — nunca
 *  some. Compartilhado entre a tabela (desktop) e o card (mobile). */
function DeviceIdentity({ device, hasIxc = false, archived = false, authStatus = "authorized" }) {
  const { label, kind } = deviceDisplayName(device);
  const isId = kind === "id";
  const showTag = kind === "pppoe" || kind === "point";
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          className={(isId ? "font-mono " : "font-medium ") + "text-noc-text break-all"}
          title={device.device_id}
        >
          {label}
        </span>
        {showTag && (
          <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded border border-noc-border/70 text-noc-faint">
            {DEVICE_NAME_KIND_LABEL[kind]}
          </span>
        )}
        <DeviceBadges hasIxc={hasIxc} device={device} archived={archived} authStatus={authStatus} />
      </div>
      {!isId && (
        <div className="font-mono text-[10px] text-noc-muted mt-0.5 break-all" title={device.device_id}>
          {device.device_id}
        </div>
      )}
    </div>
  );
}

/** NOC-UX — botões de ação (Autorizar/Bloquear/Revogar/Arquivar/Restaurar +
 *  detalhes). Compartilhado entre a tabela (desktop) e o card (mobile). */
function DeviceActionButtons({
  device, authView = false, authStatus = "authorized", archived = false,
  canArchive = false, busy = false,
  onAuthorize, onBlock, onRevoke, onArchive, onRestore, onOpenDetail,
}) {
  const sec = "text-[11px] px-2 py-1 rounded-md border disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <>
      {authView ? (
        <>
          {authStatus !== "authorized" && (
            <button type="button" onClick={() => onAuthorize?.(device)} disabled={busy}
              className={`${sec} border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10`} title="Autorizar (tornar operacional)">
              {busy ? "…" : "Autorizar"}
            </button>
          )}
          {authStatus === "pending" && (
            <button type="button" onClick={() => onBlock?.(device)} disabled={busy}
              className={`${sec} border-noc-err/30 text-noc-err hover:bg-noc-err/10`} title="Bloquear (negar)">
              {busy ? "…" : "Bloquear"}
            </button>
          )}
          {authStatus === "authorized" && (
            <button type="button" onClick={() => onRevoke?.(device)} disabled={busy}
              className={`${sec} border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10`} title="Revogar autorização (volta para pendente)">
              {busy ? "…" : "Revogar"}
            </button>
          )}
        </>
      ) : (
        canArchive &&
        (archived ? (
          <button type="button" onClick={() => onRestore?.(device)} disabled={busy}
            className={`${sec} border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10`} title="Restaurar dispositivo arquivado">
            {busy ? "…" : "Restaurar"}
          </button>
        ) : (
          <button type="button" onClick={() => onArchive?.(device)} disabled={busy}
            className={`${sec} border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10`} title="Arquivar (soft-delete) — preserva histórico">
            {busy ? "…" : "Arquivar"}
          </button>
        ))
      )}
      {/* o gráfico 24h COMPLETO vive no DeviceDetail (botão "detalhes"). */}
      <button onClick={() => onOpenDetail(device.device_id)} className="btn-primary text-xs px-3 py-1.5">
        {t("devicesList.btnDetails")}
      </button>
    </>
  );
}

/** NOC-UX-DEVICES-LIST-RESPONSIVE — card empilhado p/ telas estreitas (md:hidden).
 *  Tabela não cabe em ~390px sem cortar; aqui cada device vira um bloco que
 *  quebra linha (sem rolagem horizontal). Mesmos selos/ações da tabela. */
function DeviceMobileCard({
  device, hasIxc = false, onOpenDetail, canArchive = false, onArchive, onRestore,
  authView = false, onAuthorize, onBlock, onRevoke, busy = false,
}) {
  const relay = pickPayload(device, "relay");
  const pm = extractPowerMonitor(device.last_hb_payload);
  const archived = !!device.archived_at;
  const authStatus = device.authorization_status || "authorized";
  return (
    <div className={`px-3 py-3 ${archived || authStatus !== "authorized" ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <StatusBadge device={device} />
        <RelayBadge relay={relay} />
      </div>
      <div className="mt-2 text-xs leading-relaxed">
        <DeviceIdentity device={device} hasIxc={hasIxc} archived={archived} authStatus={authStatus} />
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-mono text-noc-muted break-all">
          {device.fw_version || "—"}
        </span>
        {pm && (
          <button
            type="button"
            onClick={() => onOpenDetail(device.device_id)}
            className={`inline-flex items-center gap-1 text-[9px] font-bold px-1 py-0.5 rounded border ${
              pm.bad
                ? "border-noc-warn/30 text-noc-warn bg-noc-warn/10"
                : "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
            }`}
            title="Energia (INA226) — abrir gráfico 24h nos detalhes"
          >
            ⚡ {powerChipText(pm)} <span aria-hidden="true">📈</span>
          </button>
        )}
        <span className="text-noc-faint">·</span>
        <LastSeenCell lastSeen={device.last_seen} />
      </div>
      <div className="mt-2.5 flex items-center justify-end gap-2 flex-wrap">
        <DeviceActionButtons
          device={device}
          authView={authView}
          authStatus={authStatus}
          archived={archived}
          canArchive={canArchive}
          busy={busy}
          onAuthorize={onAuthorize}
          onBlock={onBlock}
          onRevoke={onRevoke}
          onArchive={onArchive}
          onRestore={onRestore}
          onOpenDetail={onOpenDetail}
        />
      </div>
    </div>
  );
}

function DeviceRow({
  device,
  onOpenDetail,
  hasIxc = false,
  expanded = false,
  onToggle,
  onShowEnergy,
  canArchive = false,
  onArchive,
  onRestore,
  authView = false,
  onAuthorize,
  onBlock,
  onRevoke,
  busy = false,
}) {
  const rssi = pickPayload(device, "rssi");
  const relay = pickPayload(device, "relay");
  const uptime = pickPayload(device, "uptime_s");
  const ip = device.ip_last || pickPayload(device, "ip");
  // S7.5C-B — heap livre + razão do último reboot (payload S7.5C-A). Devices
  // antigos retornam null → "—". Agora exibidos na linha expandível.
  const heapFree = pickPayload(device, "heap_free");
  const rebootReason = pickPayload(device, "reboot_reason");
  // INA226-2B — energia (INA226) do heartbeat cru. null em device comum →
  // nada é renderizado (sem chip/linha vazia). Modelo esp32c3-ina226 → chip.
  const pm = extractPowerMonitor(device.last_hb_payload);
  // S3.11B — device arquivado (soft-delete): aparece esmaecido + badge, só
  // quando "Mostrar arquivados" está ligado.
  const archived = !!device.archived_at;
  // S3.11C — estado de autorização (só 'authorized' é operacional).
  const authStatus = device.authorization_status || "authorized";

  return (
    <>
      <tr
        className={`border-t border-noc-divider hover:bg-noc-accent/[0.04] ${
          archived || authStatus !== "authorized" ? "opacity-60" : ""
        }`}
      >
        <Td>
          <button
            type="button"
            onClick={onToggle}
            className="w-5 text-center text-noc-muted hover:text-noc-text"
            title={expanded ? "Recolher detalhes técnicos" : "Mostrar detalhes técnicos"}
            aria-expanded={expanded}
          >
            {expanded ? "▾" : "▸"}
          </button>
        </Td>
        <Td><StatusBadge device={device} /></Td>
        <Td className="whitespace-normal">
          <DeviceIdentity device={device} hasIxc={hasIxc} archived={archived} authStatus={authStatus} />
        </Td>
        <Td className="hidden lg:table-cell">
          {device.customer_code || <span className="text-noc-muted">—</span>}
        </Td>
        <Td mono>
          {device.fw_version ? (
            <span
              className="inline-block max-w-[11rem] truncate align-bottom"
              title={device.fw_version}
            >
              {device.fw_version}
            </span>
          ) : (
            <span className="text-noc-muted">—</span>
          )}
          {/* INA226-2B + ENERGY-HISTORY-VISIBILITY — chip de Energia inline, só
              p/ device compatível. Agora é BOTÃO: abre o painel Energia 24h. */}
          {pm && (
            <button
              type="button"
              onClick={onShowEnergy}
              className={`ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold px-1 py-0.5 rounded border align-middle cursor-pointer transition hover:brightness-125 ${
                pm.bad
                  ? "border-noc-warn/30 text-noc-warn bg-noc-warn/10"
                  : "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
              }`}
              title={`Energia (INA226) · ${powerStatusLabel(pm) || ""} — clique para o gráfico 24h`}
            >
              ⚡ {powerChipText(pm)} <span aria-hidden="true">📈</span>
            </button>
          )}
        </Td>
        <Td><RelayBadge relay={relay} /></Td>
        <Td><LastSeenCell lastSeen={device.last_seen} /></Td>
        <Td className="text-right">
          <div className="flex items-center justify-end gap-2">
            <DeviceActionButtons
              device={device}
              authView={authView}
              authStatus={authStatus}
              archived={archived}
              canArchive={canArchive}
              busy={busy}
              onAuthorize={onAuthorize}
              onBlock={onBlock}
              onRevoke={onRevoke}
              onArchive={onArchive}
              onRestore={onRestore}
              onOpenDetail={onOpenDetail}
            />
          </div>
        </Td>
      </tr>
      {expanded && (
        <tr className="bg-noc-bg/40 border-t border-noc-border/30">
          <td />
          <td colSpan={7} className="px-2.5 py-2">
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {/* Cliente/Relé/IP/última comunicação reaparecem aqui quando
                  escondidos por responsividade (telas estreitas) — nada se perde. */}
              <Detail label={t("devicesList.colCustomer")}>
                {device.customer_code || "—"}
              </Detail>
              <Detail label="Última comunicação"><LastSeenCell lastSeen={device.last_seen} /></Detail>
              <Detail label={t("devicesList.colIp")}>{ip || "—"}</Detail>
              <Detail label={t("devicesList.colRelay")}><RelayBadge relay={relay} /></Detail>
              <Detail label={t("devicesList.colRssi")}><RssiBadge rssi={rssi} /></Detail>
              <Detail label={t("devicesList.colUptime")}>{formatUptime(uptime)}</Detail>
              <Detail label={t("devicesList.colHeap")}><HeapBadge bytes={heapFree} /></Detail>
              <Detail label={t("devicesList.colRebootReason")}>
                {rebootReason || "—"}
              </Detail>
              {/* INA226-2B — Energia detalhada na linha expandível (só compatível). */}
              {pm && (
                <Detail label="Energia">
                  <span style={{ color: pm.bad ? "#FFB224" : "#2BD9F0" }}>
                    ⚡ {powerChipText(pm)}
                  </span>
                </Detail>
              )}
              {archived && (
                <Detail label="Arquivado por">
                  {device.archived_by || "—"}
                  {device.archive_reason ? ` — ${device.archive_reason}` : ""}
                </Detail>
              )}
            </div>
            {/* ENERGY-HISTORY-RESPONSIVE — resumo COMPACTO (chips + sparkline),
                não o gráfico grande, p/ não estourar a tabela. Container preso
                à viewport (sticky + max-w) → sem scroll horizontal extra no
                mobile. O gráfico completo fica no DeviceDetail (botão abaixo /
                link "Ver completo"). Device comum (pm null): nada. */}
            {pm && (
              <div className="sticky left-0 w-full max-w-[calc(100vw-2.5rem)] sm:max-w-2xl">
                <EnergyHistoryPanel
                  deviceId={device.device_id}
                  variant="compact"
                  onOpenDetail={() => onOpenDetail(device.device_id)}
                />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
